import React, { useState } from 'react';

const showResourcesSection = false; // Set to true to show the section, false to hide it
import { BookOpen, Video, Download, Search, Filter, Clock, User, Tag, CheckCircle, AlertTriangle } from 'lucide-react';

const Resources = () => {
  const [activeTab, setActiveTab] = useState('blog');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [showPostureQuiz, setShowPostureQuiz] = useState(false);
  const [postureQuizStep, setPostureQuizStep] = useState(0);
  const [postureQuizResults, setPostureQuizResults] = useState<number[]>([]);

  const categories = ['All', 'Sports', 'Workplace', 'Home Care', 'Pediatric'];

  // Posture Quiz Questions
  const postureQuizQuestions = [
    {
      question: "How do you typically sit at your desk?",
      options: [
        "Slouched with shoulders forward",
        "Sitting straight with feet flat on floor",
        "Cross-legged or one leg over the other",
        "Leaning back in chair"
      ],
      scores: [1, 4, 2, 3]
    },
    {
      question: "How often do you take breaks from sitting?",
      options: [
        "Every 30 minutes or less",
        "Every 1-2 hours",
        "Every 3-4 hours",
        "Rarely or never"
      ],
      scores: [4, 3, 2, 1]
    },
    {
      question: "Where is your computer screen positioned?",
      options: [
        "At eye level, arm's length away",
        "Below eye level",
        "Too close to face",
        "Too far away"
      ],
      scores: [4, 2, 1, 2]
    }
  ];

  const handlePostureQuizAnswer = (score: number) => {
    const newResults = [...postureQuizResults, score];
    setPostureQuizResults(newResults);
    
    if (postureQuizStep < postureQuizQuestions.length - 1) {
      setPostureQuizStep(postureQuizStep + 1);
    } else {
      // Quiz completed
      const totalScore = newResults.reduce((sum, score) => sum + score, 0);
      const maxScore = postureQuizQuestions.length * 4;
      const percentage = (totalScore / maxScore) * 100;
      
      // Store result for display
      setPostureQuizResults([...newResults, percentage]);
    }
  };

  const getPostureQuizResult = () => {
    const percentage = postureQuizResults[postureQuizResults.length - 1];
    if (percentage >= 80) {
      return {
        title: "Excellent Posture!",
        message: "Your posture habits are great. Keep up the good work!",
        color: "text-green-600",
        bgColor: "bg-green-50"
      };
    } else if (percentage >= 60) {
      return {
        title: "Good Posture",
        message: "Your posture is generally good, but there's room for improvement.",
        color: "text-blue-600",
        bgColor: "bg-blue-50"
      };
    } else {
      return {
        title: "Needs Improvement",
        message: "Consider ergonomic adjustments and regular breaks to improve your posture.",
        color: "text-orange-600",
        bgColor: "bg-orange-50"
      };
    }
  };

  const resetPostureQuiz = () => {
    setPostureQuizStep(0);
    setPostureQuizResults([]);
    setShowPostureQuiz(false);
  };

  const blogPosts = [
    {
      title: "How to Prevent Common Sports Injuries",
      excerpt: "Learn practical tips to avoid injuries during sports and exercise, from warm-ups to proper technique.",
      category: "Sports",
      readTime: "5 min read",
      author: "Dr. Deep Maheshwari",
      authorLink: "https://www.linkedin.com/in/dr-deep-maheshwari-pt-4b369a196",
      image: "https://images.pexels.com/photos/4506109/pexels-photo-4506109.jpeg?auto=compress&cs=tinysrgb&w=400",
      date: "April 10, 2024"
    },
    {
      title: "Desk Ergonomics: A Guide for Office Workers",
      excerpt: "Set up your workstation to reduce back and neck pain. Includes a printable checklist!",
      category: "Workplace",
      readTime: "6 min read",
      author: "Dr. Pragati Nayak",
      authorLink: "#",
      image: "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=400",
      date: "April 5, 2024"
    },
    {
      title: "Exercises for Knee Pain Relief at Home",
      excerpt: "Simple, safe exercises to strengthen your knees and reduce pain, suitable for all ages.",
      category: "Home Care",
      readTime: "7 min read",
      author: "Dr. Pragati Nayak",
      authorLink: "#",
      image: "https://images.pexels.com/photos/7551544/pexels-photo-7551544.jpeg?auto=compress&cs=tinysrgb&w=400",
      date: "March 28, 2024"
    },
    {
      title: "Pediatric Physiotherapy: Supporting Child Development",
      excerpt: "How early intervention and therapy can help children with developmental delays thrive.",
      category: "Pediatric",
      readTime: "5 min read",
      author: "Dr. Deep Maheshwari",
      authorLink: "https://www.linkedin.com/in/dr-deep-maheshwari-pt-4b369a196",
      image: "https://images.pexels.com/photos/8613061/pexels-photo-8613061.jpeg?auto=compress&cs=tinysrgb&w=400",
      date: "March 20, 2024"
    }
  ];

  const videos = [
    {
      title: "From Setback to Comeback: ACL Recovery Journey",
      duration: "10:30",
      category: "Sports",
      thumbnail: "https://images.pexels.com/photos/4506109/pexels-photo-4506109.jpeg?auto=compress&cs=tinysrgb&w=400",
      views: "2.1K views",
      url: "https://youtu.be/oR-288PuDhc"
    },
    {
      title: "Role of Physiotherapy & Scope of Practice",
      duration: "15:45",
      category: "Education",
      thumbnail: "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=400",
      views: "1.8K views",
      url: "https://youtu.be/oR-288PuDhc"
    },
    {
      title: "Stepping Ahead at Ease — Ankle Mobility Exercises",
      duration: "12:20",
      category: "Exercise",
      thumbnail: "https://images.pexels.com/photos/7551544/pexels-photo-7551544.jpeg?auto=compress&cs=tinysrgb&w=400",
      views: "3.2K views",
      url: "https://www.instagram.com/reel/DEAQffbTX6N/"
    },
    {
      title: "This Year Has Been a Testament... (Annual Reflection)",
      duration: "8:15",
      category: "Clinic",
      thumbnail: "https://images.pexels.com/photos/8613061/pexels-photo-8613061.jpeg?auto=compress&cs=tinysrgb&w=400",
      views: "1.5K views",
      url: "https://www.instagram.com/arpan.physiocure/reel/DESUDA5zQxt/"
    },
    {
      title: "Wishing Our Founder a Happy Birthday (Clinic Story)",
      duration: "5:30",
      category: "Clinic",
      thumbnail: "https://images.pexels.com/photos/8613061/pexels-photo-8613061.jpeg?auto=compress&cs=tinysrgb&w=400",
      views: "1.2K views",
      url: "https://www.instagram.com/p/DMYS1hJTFT0/"
    }
  ];

  const downloads = [
    {
      title: "Posture Check & Desk Ergonomics",
      description: "A comprehensive guide to help you set up your workstation for better posture and less pain.",
      category: "Workplace",
      fileType: "PDF",
      fileSize: "0.5 MB",
      downloads: "Free"
    },
    {
      title: "Home Exercise Program: Spine & Neck Care",
      description: "Step-by-step illustrated guide for safe spine and neck exercises at home.",
      category: "Home Care",
      fileType: "PDF",
      fileSize: "1.2 MB",
      downloads: "Free"
    },
    {
      title: "Corporate Stretch Break Planner",
      description: "A printable guide with stretch exercises for office workers to use during breaks.",
      category: "Workplace",
      fileType: "PDF",
      fileSize: "0.8 MB",
      downloads: "Free"
    },
    {
      title: "Corporate Wellness Program Overview",
      description: "Comprehensive overview of our corporate wellness programs with ROI statistics and case studies.",
      category: "Corporate",
      fileType: "PDF",
      fileSize: "1.5 MB",
      downloads: "Free"
    }
  ];

  const filteredContent = (content: any[]) => {
    return content.filter(item => {
      const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          item.excerpt?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'All' || item.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  };

  const tabs = [
    { id: 'blog', label: 'Blog Articles', icon: BookOpen },
    { id: 'videos', label: 'Video Tutorials', icon: Video },
    { id: 'downloads', label: 'Resources', icon: Download },
    { id: 'tools', label: 'Interactive Tools', icon: CheckCircle }
  ];

  return (
    showResourcesSection && (
    <section id="resources" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Health & Wellness Resources
          </h2>
          <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Expert insights, practical guides, and interactive tools to help you 
            maintain optimal health and prevent injuries.
          </p>
        </div>

        {/* Search and Filter */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-12">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search resources..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent text-sm sm:text-base"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="pl-10 pr-8 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent appearance-none bg-white text-sm sm:text-base"
              >
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-12">
          <div className="bg-white rounded-2xl sm:rounded-full p-2 shadow-lg overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-3 sm:px-6 py-2 sm:py-3 rounded-full font-semibold transition-all duration-300 text-sm sm:text-base whitespace-nowrap ${
                  activeTab === tab.id 
                    ? 'bg-sky-600 text-white shadow-lg' 
                    : 'text-gray-600 hover:text-sky-600'
                }`}
              >
                <tab.icon className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="min-h-[600px]">
          {/* Interactive Tools */}
          {activeTab === 'tools' && (
            <div className="space-y-8">
              {/* Posture Check Quiz */}
              <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Posture Check Quiz</h3>
                <p className="text-gray-600 mb-6">Take this quick quiz to assess your posture habits and get personalized recommendations.</p>
                
                {!showPostureQuiz ? (
                  <button
                    onClick={() => setShowPostureQuiz(true)}
                    className="bg-sky-600 hover:bg-sky-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200"
                  >
                    Start Posture Quiz
                  </button>
                ) : (
                  <div className="space-y-6">
                    {postureQuizStep < postureQuizQuestions.length ? (
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">
                          Question {postureQuizStep + 1} of {postureQuizQuestions.length}
                        </h4>
                        <p className="text-gray-700 mb-4">{postureQuizQuestions[postureQuizStep].question}</p>
                        <div className="space-y-3">
                          {postureQuizQuestions[postureQuizStep].options.map((option, index) => (
                            <button
                              key={index}
                              onClick={() => handlePostureQuizAnswer(postureQuizQuestions[postureQuizStep].scores[index])}
                              className="w-full text-left p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                            >
                              {option}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className={`p-6 rounded-lg ${getPostureQuizResult().bgColor}`}>
                        <h4 className={`text-xl font-bold mb-2 ${getPostureQuizResult().color}`}>
                          {getPostureQuizResult().title}
                        </h4>
                        <p className="text-gray-700 mb-4">{getPostureQuizResult().message}</p>
                        <button
                          onClick={resetPostureQuiz}
                          className="bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors duration-200"
                        >
                          Take Quiz Again
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Quick Downloads */}
              <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">Quick Downloads</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  <a
                    href="#"
                    className="block p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    <Download className="w-8 h-8 text-sky-600 mb-2" />
                    <h4 className="font-semibold text-gray-900">Posture Check & Desk Ergonomics</h4>
                    <p className="text-sm text-gray-600">PDF • 0.5 MB</p>
                  </a>
                  <a
                    href="#"
                    className="block p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    <Download className="w-8 h-8 text-sky-600 mb-2" />
                    <h4 className="font-semibold text-gray-900">Home Exercise Program: Spine & Neck Care</h4>
                    <p className="text-sm text-gray-600">PDF • 1.2 MB</p>
                  </a>
                  <a
                    href="#"
                    className="block p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    <Download className="w-8 h-8 text-sky-600 mb-2" />
                    <h4 className="font-semibold text-gray-900">Corporate Stretch Break Planner</h4>
                    <p className="text-sm text-gray-600">PDF • 0.8 MB</p>
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* Blog Posts */}
          {activeTab === 'blog' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {filteredContent(blogPosts).map((post, index) => (
                <article key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group">
                  <div className="relative">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-sky-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                        {post.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-4 sm:p-6">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 group-hover:text-sky-600 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-sm sm:text-base text-gray-600 mb-4">{post.excerpt}</p>
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center">
                        <User className="w-4 h-4 mr-1" />
                        <a 
                          href={post.authorLink} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="hover:underline"
                        >
                          {post.author}
                        </a>
                      </div>
                      <div className="flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {post.readTime}
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t">
                      <button className="text-sky-600 hover:text-sky-700 font-semibold flex items-center">
                        Read More
                        <BookOpen className="w-4 h-4 ml-2" />
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}

          {/* Videos */}
          {activeTab === 'videos' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {filteredContent(videos).map((video, index) => (
                <a 
                  key={index} 
                  href={video.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 group cursor-pointer"
                >
                  <div className="relative">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-48 object-cover"
                    />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="bg-white/90 p-4 rounded-full group-hover:scale-110 transition-transform duration-300">
                        <Video className="w-8 h-8 text-sky-600" />
                      </div>
                    </div>
                    <div className="absolute bottom-4 right-4 bg-black/70 text-white px-2 py-1 rounded text-sm">
                      {video.duration}
                    </div>
                    <div className="absolute top-4 left-4">
                      <span className="bg-emerald-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                        {video.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-4 sm:p-6">
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 group-hover:text-sky-600 transition-colors">
                      {video.title}
                    </h3>
                    <p className="text-gray-500 text-sm">{video.views}</p>
                  </div>
                </a>
              ))}
            </div>
          )}

          {/* Downloads */}
          {activeTab === 'downloads' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
              {filteredContent(downloads).map((resource, index) => (
                <div key={index} className="bg-white rounded-xl shadow-lg p-4 sm:p-6 hover:shadow-xl transition-all duration-300 group">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-3">
                        <div className="bg-emerald-100 p-2 rounded-lg mr-3">
                          <Download className="w-6 h-6 text-emerald-600" />
                        </div>
                        <div>
                          <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs font-semibold">
                            {resource.fileType}
                          </span>
                          <span className="ml-2 text-gray-500 text-sm">{resource.fileSize}</span>
                        </div>
                      </div>
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 group-hover:text-sky-600 transition-colors">
                        {resource.title}
                      </h3>
                      <p className="text-sm sm:text-base text-gray-600 mb-4">{resource.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center text-sm text-gray-500">
                          <Tag className="w-4 h-4 mr-1" />
                          {resource.category}
                        </div>
                        <div className="text-sm text-gray-500">
                          {resource.downloads}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 pt-4 border-t">
                    <button className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-2 sm:py-3 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center text-sm sm:text-base">
                      <Download className="w-5 h-5 mr-2" />
                      Download Free
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Interactive Tool CTA */}
        <div className="mt-12 sm:mt-16 bg-gradient-to-r from-sky-100 to-emerald-100 rounded-2xl p-6 sm:p-8 md:p-12 text-center">
          <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">Need Personalized Advice?</h3>
          <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 max-w-2xl mx-auto">
            Try our interactive assessment tools or schedule a consultation with our experts 
            for personalized health recommendations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => setActiveTab('tools')}
              className="bg-sky-600 hover:bg-sky-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold transition-colors duration-200"
            >
              Posture Assessment
            </button>
            <button className="border-2 border-sky-600 text-sky-600 hover:bg-sky-600 hover:text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold transition-all duration-200">
              Book Consultation
            </button>
          </div>
        </div>
      </div>
    </section>
    )
  );
};

export default Resources;